// AnnouncementsManager.jsx
import React, { useEffect, useRef, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { collection, getDocs, query, where, orderBy } from "firebase/firestore";
import AnnouncementPopupModal from "../AnnouncementPopupModal/AnnouncementPopupModal";
import useUserStore, { selectUserDetails, selectUserId } from "../../../store/user/user";
import useGlobalStore from "../../../store/store";
import { db } from "../../../services/firebaseService/connection";

const AnnouncementsManager = () => {
  const user = useUserStore(selectUserDetails);
  const userId = useUserStore(selectUserId);
  const hasShownAlertThisSession = useRef(false);

  const setCurrentAlert = useGlobalStore(
    (state) => state.alerts.setCurrentAlert,
  );
  const setPopupVisible = useGlobalStore(
    (state) => state.alerts.setPopupVisible,
  );
  const setUnreadCount = useGlobalStore((state) => state.alerts.setUnreadCount);

  // Get user's group IDs for filtering
  const userGroupIds = useMemo(
    () =>
      user?.userRoles
        ? Object.keys(user.userRoles).filter(
            (groupId) => user.userRoles[groupId] === "admin",
          )
        : [],
    [user?.userRoles],
  );

  // Fetch all active alerts
  const { data: allAlerts = [], isLoading: alertsLoading } = useQuery({
    queryKey: ["adminAlerts", userGroupIds],
    queryFn: async () => {
      const alertsRef = collection(db, "adminAlerts");
      const q = query(
        alertsRef,
        where("isActive", "==", true),
        orderBy("priority", "desc"),
        orderBy("createdAt", "desc"),
      );
      const snapshot = await getDocs(q);

      return snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
    },
    enabled: !!userId && userGroupIds.length > 0,
  });

  // Fetch user's viewed alerts
  const { data: viewedAlertIds = [], isLoading: viewedLoading } = useQuery({
    queryKey: ["userViewedAlerts", userId],
    queryFn: async () => {
      const viewedRef = collection(
        db,
        "adminAlertViews",
        userId,
        "viewedAlerts",
      );
      const snapshot = await getDocs(viewedRef);

      return snapshot.docs.map((doc) => doc.id);
    },
    enabled: !!userId,
  });

  const isLoading = alertsLoading || viewedLoading;

  // Filter alerts: unviewed + (global OR targeted to user's groups)
  const unseenAlerts = useMemo(() => {
    return allAlerts.filter((alert) => {
      if (viewedAlertIds.includes(alert.id)) return false;
      if (!alert.targetType || alert.targetType === "global") return true;
      if (alert.targetType === "group_specific") {
        return alert.targetGroupIds?.some((groupId) =>
          userGroupIds.includes(groupId),
        );
      }
      return false;
    });
  }, [allAlerts, viewedAlertIds, userGroupIds]);

  useEffect(() => {
    setUnreadCount(unseenAlerts.length);
  }, [unseenAlerts.length]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (
      !hasShownAlertThisSession.current &&
      !isLoading &&
      unseenAlerts.length > 0
    ) {
      setCurrentAlert(unseenAlerts[0]);
      setPopupVisible(true);
      hasShownAlertThisSession.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unseenAlerts, isLoading]);

  return <>{userId && <AnnouncementPopupModal isLoading={isLoading} />}</>;
};

export default AnnouncementsManager;
